import cn from 'classnames'
import { useMemo } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'

import { TSType } from '../../models'

import {
  JBP_NON_POWER,
  JBP_POWER,
  JBP_POWER_TOTAL,
  JBP_SOB,
  subTitleTranslation as tr,
} from './translations'

import styles from './CategorySubTitle.module.scss'

interface IProps {
  isShowQuotas?: boolean
  isShowActuals?: boolean
  isShowActualsSplit?: boolean
  isShowSmbVsEuSplit?: boolean
  isShowSubTitles?: boolean

  isShowYoYActualsVsActuals: boolean
  isShowYoYActualsVsPlans: boolean
  isShowYoYPlansVsPlans: boolean
  isShowYoYQuotaVsActuals: boolean
  isShowYoYPartnersVsPartners: boolean

  isShowPlanAchievement: boolean
  isShowQuotaAchievement: boolean

  isShowQoQActualsVsActuals: boolean
  isShowQoQActualsVsPlans: boolean
  isShowQoQPlansVsPlans: boolean
  isShowQoQQuotaVsActuals: boolean
  isShowQoQPartnersVsPartners: boolean

  isDistributorPlanner: boolean
  isShowPartnerCount: boolean

  isEditRow?: boolean
  isSob?: boolean
  sType: TSType
}

export const CategorySubTitle = ({
  isShowQuotas,
  isShowActuals,
  isShowActualsSplit,
  isShowSmbVsEuSplit,
  isShowSubTitles,

  isShowYoYActualsVsActuals,
  isShowYoYActualsVsPlans,
  isShowYoYPlansVsPlans,
  isShowYoYQuotaVsActuals,
  isShowYoYPartnersVsPartners,

  isShowPlanAchievement,
  isShowQuotaAchievement,

  isShowQoQActualsVsActuals,
  isShowQoQActualsVsPlans,
  isShowQoQPlansVsPlans,
  isShowQoQQuotaVsActuals,
  isShowQoQPartnersVsPartners,

  isDistributorPlanner,
  isShowPartnerCount,

  isEditRow,
  isSob,
  sType,
}: IProps) => {
  /** -------------------- Variables ----------------------- */

  const isShowAnyYoY = useMemo(() => {
    return (
      isShowYoYActualsVsActuals ||
      isShowYoYActualsVsPlans ||
      isShowYoYPlansVsPlans ||
      (!isSob && isShowYoYQuotaVsActuals)
    )
  }, [
    isShowYoYActualsVsActuals,
    isShowYoYActualsVsPlans,
    isShowYoYPlansVsPlans,
    isShowYoYQuotaVsActuals,
    isSob,
  ])

  const isShowAnyAchievement = useMemo(() => {
    return (!isSob && isShowPlanAchievement) || (!isSob && isShowQuotaAchievement)
  }, [isShowPlanAchievement, isShowQuotaAchievement, isSob])

  const isShowAnyQoQ = useMemo(() => {
    return (
      isShowQoQActualsVsActuals ||
      isShowQoQActualsVsPlans ||
      isShowQoQPlansVsPlans ||
      (!isSob && isShowQoQQuotaVsActuals)
    )
  }, [
    isShowQoQActualsVsActuals,
    isShowQoQActualsVsPlans,
    isShowQoQPlansVsPlans,
    isShowQoQQuotaVsActuals,
    isSob,
  ])

  const isShowAnyPartnerCount = useMemo(() => {
    return isDistributorPlanner && isShowPartnerCount && !isSob
  }, [isDistributorPlanner, isShowPartnerCount, isSob])

  const isShowCountPercent = useMemo(() => {
    return isDistributorPlanner && isShowPartnerCount && !isSob && !!sType
  }, [isDistributorPlanner, isShowPartnerCount, isSob, sType])

  const translateSuffix = useMemo(() => {
    let suffix = ''

    if (isDistributorPlanner && !isSob) {
      if (!sType) {
        suffix = JBP_POWER_TOTAL
      } else {
        suffix = sType === 'c' ? JBP_NON_POWER : JBP_POWER
      }
    }
    if (isSob) {
      suffix += JBP_SOB
    }

    return suffix
  }, [isDistributorPlanner, isSob, sType])

  const qoqTranslations = tr.qoq
  const partnerQoqTranslation = tr.partner.qoq

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      {!!isShowSubTitles && (
        <div className={styles.subTitles}>
          {!isSob && !!isShowQuotas && (
            <div className={cn(styles.subTitle, styles.quotaColor)}>
              <Translation text={`${tr.quota}${translateSuffix}`} />
            </div>
          )}
          {!!isShowActuals && (
            <div className={cn(styles.subTitle, styles.actualsColor)}>
              <Translation text={`${tr.actuals}${translateSuffix}`} />
            </div>
          )}
          {!!isShowActualsSplit && !!isShowActuals && (
            <>
              <div className={cn(styles.subTitle, styles.actualsColor)}>
                <Translation text={`${tr.actualsDirect}${translateSuffix}`} />
              </div>
              <div className={cn(styles.subTitle, styles.actualsColor)}>
                <Translation text={`${tr.actualsIndirect}${translateSuffix}`} />
              </div>
            </>
          )}
          {!!isShowSmbVsEuSplit && !!isShowActuals && (
            <>
              <div className={cn(styles.subTitle, styles.actualsColor)}>
                <Translation text={`${tr.actualsSmb}${translateSuffix}`} />
              </div>
              <div className={cn(styles.subTitle, styles.actualsColor)}>
                <Translation text={`${tr.actualsEu}${translateSuffix}`} />
              </div>
            </>
          )}
          <div
            className={cn(styles.subTitle, styles.targetsColor, {
              [styles.editRow]: isEditRow,
              [styles.sobEditRow]: isEditRow && isSob,
            })}
          >
            <Translation text={`${tr.targets}${translateSuffix}`} />
          </div>

          {!!isShowSmbVsEuSplit && (
            <>
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.targetsSmb}${translateSuffix}`} />
              </div>
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.targetsEu}${translateSuffix}`} />
              </div>
            </>
          )}
          {isShowAnyPartnerCount && (
            <div className={cn({ [styles.yoyBlock]: isShowAnyAchievement })}>
              <div className={cn(styles.subTitle, styles.actualsColor)}>
                <Translation text={`${tr.partner.count}${translateSuffix}`} />
              </div>
              {isShowCountPercent && (
                <div className={cn(styles.subTitle, styles.targetsColor)}>
                  <Translation text={`${tr.partner.percent}${translateSuffix}`} />
                </div>
              )}
              {isShowYoYPartnersVsPartners && (
                <div className={cn(styles.subTitle, styles.targetsColor)}>
                  <Translation text={`${tr.partner.yoy}${translateSuffix}`} />
                </div>
              )}
              {isShowQoQPartnersVsPartners && (
                <div className={cn(styles.subTitle, styles.targetsColor)}>
                  <Translation text={`${partnerQoqTranslation}${translateSuffix}`} />
                </div>
              )}
            </div>
          )}

          <div className={cn({ [styles.yoyBlock]: isShowAnyYoY })}>
            {isShowYoYActualsVsActuals && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.yoy.ava}${translateSuffix}`} />
              </div>
            )}
            {isShowYoYActualsVsPlans && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.yoy.pva}${translateSuffix}`} />
              </div>
            )}
            {isShowYoYPlansVsPlans && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.yoy.pvp}${translateSuffix}`} />
              </div>
            )}
            {!isSob && isShowYoYQuotaVsActuals && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.yoy.qva}${translateSuffix}`} />
              </div>
            )}
          </div>

          <div className={cn({ [styles.yoyBlock]: isShowAnyAchievement })}>
            {!isSob && isShowPlanAchievement && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${tr.achiev.targets}${translateSuffix}`} />
              </div>
            )}
            {!isSob && isShowQuotaAchievement && (
              <div className={cn(styles.subTitle, styles.quotaColor)}>
                <Translation text={`${tr.achiev.quota}${translateSuffix}`} />
              </div>
            )}
          </div>

          <div className={cn({ [styles.yoyBlock]: isShowAnyQoQ })}>
            {isShowQoQActualsVsActuals && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${qoqTranslations.ava}${translateSuffix}`} />
              </div>
            )}
            {isShowQoQActualsVsPlans && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${qoqTranslations.pva}${translateSuffix}`} />
              </div>
            )}
            {isShowQoQPlansVsPlans && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${qoqTranslations.pvp}${translateSuffix}`} />
              </div>
            )}
            {!isSob && isShowQoQQuotaVsActuals && (
              <div className={cn(styles.subTitle, styles.targetsColor)}>
                <Translation text={`${qoqTranslations.qva}${translateSuffix}`} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
